import React, { useState } from "react";
import {
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Card,
  TextField,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import Grid2 from "@mui/material/Grid2";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FormatLineSpacingOutlinedIcon from "@mui/icons-material/FormatLineSpacingOutlined";
import dayjs from "dayjs";
import {
  samples,
  sampleTypes,
  sampleCollectionMethods,
} from "../Utility/Constants";
import GenericTextField from "../common/GenericTextField";
import { saveFormDataToLocal } from "../Utility/FormDataTempStorage";
import { useTheme, useMediaQuery } from "@mui/material";

function LaboratoryTestReportDisplay({
  formData,
  readOnly,
  setFormData,
  uniqueId,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [referenceDialog, setReferenceDialog] = useState({
    open: false,
    title: "",
    text: "",
  });

  const handleFormDataChange = (updatedValues) => {
    saveFormDataToLocal(uniqueId, updatedValues);
  };

  const getResultStatus = (value, lowerLimit, upperLimit) => {
    if (!value || lowerLimit === undefined || upperLimit === undefined)
      return "normal";
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "normal";
    if (numValue < lowerLimit) return "low";
    if (numValue > upperLimit) return "high";
    return "normal";
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case "high":
        return "error";
      case "low":
        return "warning";
      case "normal":
        return "success";
      default:
        return "default";
    }
  };

  const handleReferenceClick = (testName, referenceText, event) => {
    event.stopPropagation();
    setReferenceDialog({ open: true, title: testName, text: referenceText });
  };

  const handleReferenceClose = () => {
    setReferenceDialog({ open: false, title: "", text: "" });
  };

  const updateParameter = (testId, parameterId, fieldName, value) => {
    const updatedFormData = {
      ...formData,
      tests: formData?.tests?.map((t) => {
        if (t.testId === testId) {
          return {
            ...t,
            parameters: t?.parameters?.map((p) =>
              p.parameterId === parameterId ? { ...p, [fieldName]: value } : p
            ),
          };
        }
        return t;
      }),
    };
    setFormData(updatedFormData);
    handleFormDataChange(updatedFormData);
  };

  return (
    <Grid2 container size={12} spacing={2}>
      {/* Report Details */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">Report Details</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Sample Collected: </strong>
                    {formData?.sampleCollectionDateTime
                      ? dayjs(formData.sampleCollectionDateTime).format("DD-MMM-YYYY")
                      : "Not available"}
                  </Typography>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Sample Collected"
                      value={
                        formData?.sampleCollectionDateTime
                          ? dayjs(formData.sampleCollectionDateTime)
                          : null
                      }
                      onChange={(newDate) => {
                        const updated = {
                          ...formData,
                          sampleCollectionDateTime: newDate
                            ? newDate.toISOString()
                            : "",
                        };
                        setFormData(updated);
                        handleFormDataChange(updated);
                      }}
                      sx={{ backgroundColor: "white" }}
                      format="DD-MMM-YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          margin: "dense",
                          fullWidth: true,
                        },
                      }}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Report Date: </strong>
                    {formData?.sampleCollectionReportDate
                      ? dayjs(formData.sampleCollectionReportDate).format("DD-MMM-YYYY")
                      : "Not available"}
                  </Typography>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Report Date"
                      value={
                        formData?.sampleCollectionReportDate
                          ? dayjs(formData.sampleCollectionReportDate)
                          : null
                      }
                      onChange={(newDate) => {
                        const updated = {
                          ...formData,
                          sampleCollectionReportDate: newDate
                            ? newDate.toISOString()
                            : "",
                        };
                        setFormData(updated);
                        handleFormDataChange(updated);
                      }}
                      format="DD-MMM-YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          margin: "dense",
                          fullWidth: true,
                          sx: { backgroundColor: "white" },
                        },
                      }}
                      maxDate={dayjs()}
                    />
                  </LocalizationProvider>
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Sample Collected By: </strong>
                    {formData?.sampleCollectionPerformedBy || "Not available"}
                  </Typography>
                ) : (
                  <GenericTextField
                    label="Sample Collected By"
                    value={formData?.sampleCollectionPerformedBy || ""}
                    onChange={(e) => {
                      const updated = {
                        ...formData,
                        sampleCollectionPerformedBy: e.target.value,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    fullWidth
                    placeholder="Enter person who collected sample"
                  />
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Collection Location: </strong>
                    {formData?.sampleCollectionLocation || "Not available"}
                  </Typography>
                ) : (
                  <GenericTextField
                    label="Sample Collection Location"
                    value={formData?.sampleCollectionLocation || ""}
                    onChange={(e) => {
                      const updated = {
                        ...formData,
                        sampleCollectionLocation: e.target.value,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    fullWidth
                    placeholder="Enter collection location"
                  />
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Sample: </strong>
                    {samples.find((s) => s.id === formData?.sample)?.name ||
                      formData?.sample ||
                      "Not available"}
                  </Typography>
                ) : (
                  <Autocomplete
                    options={samples}
                    getOptionLabel={(option) => option.name}
                    value={
                      formData?.sample
                        ? samples.find((s) => s.id === formData.sample) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      const updated = {
                        ...formData,
                        sample: selectedOption ? selectedOption.id : "",
                        sampleType: "",
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample"
                        placeholder="Select sample"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Sample Type: </strong>
                    {sampleTypes.find((t) => t.value === formData?.sampleType)
                      ?.displayValue ||
                      formData?.sampleType ||
                      "Not available"}
                  </Typography>
                ) : (
                  <Autocomplete
                    options={sampleTypes.filter((type) =>
                      formData?.sample
                        ? type.category ===
                          samples.find((s) => s.id === formData.sample)
                            ?.category
                        : true
                    )}
                    getOptionLabel={(option) => option.displayValue}
                    value={
                      formData?.sampleType
                        ? sampleTypes.find(
                            (type) => type.value === formData.sampleType
                          ) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      const updated = {
                        ...formData,
                        sampleType: selectedOption ? selectedOption.value : "",
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample Type"
                        placeholder="Select sample type"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                )}
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
                {readOnly ? (
                  <Typography>
                    <strong>Collection Method: </strong>
                    {sampleCollectionMethods.find(
                      (m) => m.value === formData?.sampleCollectionMethod
                    )?.displayValue ||
                      formData?.sampleCollectionMethod ||
                      "Not available"}
                  </Typography>
                ) : (
                  <Autocomplete
                    options={sampleCollectionMethods}
                    getOptionLabel={(option) => option.displayValue}
                    value={
                      formData?.sampleCollectionMethod
                        ? sampleCollectionMethods.find(
                            (method) =>
                              method.value === formData.sampleCollectionMethod
                          ) || null
                        : null
                    }
                    onChange={(_, selectedOption) => {
                      const updated = {
                        ...formData,
                        sampleCollectionMethod: selectedOption
                          ? selectedOption.value
                          : "",
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sample Collection Method"
                        placeholder="Select collection method"
                        variant="outlined"
                        size="small"
                        sx={{ background: "#fff", borderRadius: 2 }}
                      />
                    )}
                  />
                )}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Test Selection & Results */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">Test Selection & Results</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "8px" }}>
            <Grid2 container size={12} spacing={1}>
              {/* Tests Accordions */}
              <Grid2 size={12}>
                {formData?.tests?.map((test, index) => (
                  <Accordion key={index}>
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        },
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {test.testName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(event) =>
                          handleReferenceClick(
                            test.testName,
                            test.referenceText,
                            event
                          )
                        }
                        sx={{ marginLeft: "auto", marginRight: 1 }}
                      >
                        <FormatLineSpacingOutlinedIcon fontSize="small" />
                      </IconButton>
                    </AccordionSummary>
                    <AccordionDetails>
                      {isMobile ? (
                        // Mobile view - Cards
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          {test?.parameters?.map((parameter, parameterIndex) => {
                            const status = getResultStatus(
                              parameter.resultValue,
                              parameter.lowerLimit,
                              parameter.upperLimit
                            );
                            return (
                              <Card variant="outlined" sx={{ p: 2 }} key={parameterIndex}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {parameter.parameterName}
                                    </Typography>
                                    {!readOnly && (
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={parameter.selected || false}
                                            onChange={(e) =>
                                              updateParameter(test.testId, parameter.parameterId, "selected", e.target.checked)
                                            }
                                          />
                                        }
                                        label="Select"
                                      />
                                    )}
                                  </Box>
                                  <TextField
                                    label="Result Value"
                                    value={parameter.resultValue || ""}
                                    onChange={(e) =>
                                      updateParameter(test.testId, parameter.parameterId, "resultValue", e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    placeholder="Enter result value"
                                    disabled={readOnly || !parameter.selected}
                                  />
                                  <Box sx={{ display: "flex", gap: 1 }}>
                                    <TextField
                                      label="Unit"
                                      value={parameter.unit || ""}
                                      size="small"
                                      sx={{ flex: 1 }}
                                      disabled
                                      InputProps={{ readOnly: true }}
                                    />
                                    <TextField
                                      label="Normal Range"
                                      value={`${parameter.lowerLimit} - ${parameter.upperLimit}`}
                                      size="small"
                                      sx={{ flex: 1 }}
                                      disabled
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" color="textSecondary">Status:</Typography>
                                    <Chip label={status} color={getStatusChipColor(status)} size="small" />
                                  </Box>
                                  <TextField
                                    label="Comments"
                                    value={parameter.notes || ""}
                                    onChange={(e) =>
                                      updateParameter(test.testId, parameter.parameterId, "notes", e.target.value)
                                    }
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    placeholder="Add comments"
                                    disabled={readOnly || !parameter.selected}
                                  />
                                </Box>
                              </Card>
                            );
                          })}
                        </Box>
                      ) : (
                        // Desktop view - Table
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {!readOnly && <TableCell>Select</TableCell>}
                              <TableCell>Test Name</TableCell>
                              <TableCell>Result Value</TableCell>
                              <TableCell>Unit</TableCell>
                              <TableCell>Normal Range</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Comments</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {test?.parameters?.map((parameter, parameterIndex) => {
                              const status = getResultStatus(
                                parameter.resultValue,
                                parameter.lowerLimit,
                                parameter.upperLimit
                              );
                              return (
                                <TableRow
                                  key={parameterIndex}
                                  sx={{
                                    "&:hover": { backgroundColor: "transparent !important" },
                                    "&.Mui-selected": { backgroundColor: "transparent !important" },
                                    "&.Mui-selected:hover": { backgroundColor: "transparent !important" },
                                  }}
                                >
                                  {!readOnly && (
                                    <TableCell>
                                      <Checkbox
                                        checked={parameter.selected || false}
                                        onChange={(e) =>
                                          updateParameter(test.testId, parameter.parameterId, "selected", e.target.checked)
                                        }
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>{parameter.parameterName}</TableCell>
                                  <TableCell>
                                    <TextField
                                      value={parameter.resultValue || ""}
                                      onChange={(e) =>
                                        updateParameter(test.testId, parameter.parameterId, "resultValue", e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      placeholder="Enter result value"
                                      disabled={readOnly || !parameter.selected}
                                    />
                                  </TableCell>
                                  <TableCell>{parameter.unit}</TableCell>
                                  <TableCell>
                                    {parameter.lowerLimit} - {parameter.upperLimit}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={status}
                                      color={getStatusChipColor(status)}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <TextField
                                      value={parameter.notes || ""}
                                      onChange={(e) =>
                                        updateParameter(test.testId, parameter.parameterId, "notes", e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      multiline
                                      rows={2}
                                      placeholder="Add comments"
                                      disabled={readOnly || !parameter.selected}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Summary & Interpretation */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
            <Typography component="span">Summary & Interpretation</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "16px" }}>
            <Grid2 container size={12} spacing={2}>
              {/* Abnormal Results Summary */}
              <Grid2 size={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Abnormal Results Summary
                </Typography>
                {(() => {
                  const abnormalResults = [];
                  formData?.tests?.forEach((test) => {
                    (test.parameters || [])?.forEach((parameter) => {
                      if (parameter.selected) {
                        const status = getResultStatus(
                          parameter.resultValue,
                          parameter.lowerLimit,
                          parameter.upperLimit
                        );
                        if (status !== "normal") {
                          abnormalResults.push({
                            testName: parameter.parameterName,
                            value: parameter.resultValue,
                            unit: parameter.unit,
                            status: status,
                            normalRange: `${parameter.lowerLimit} - ${parameter.upperLimit}`,
                            notes: parameter.notes,
                          });
                        }
                      }
                    });
                  });

                  return abnormalResults.length > 0 ? (
                    <ul>
                      {abnormalResults?.map((result, index) => (
                        <li key={index}>
                          <Typography variant="body2">
                            <strong>{result.testName}</strong>: {result.value}{" "}
                            {result.unit} ({result.status.toUpperCase()}) - Normal:{" "}
                            {result.normalRange}
                            {result.notes && (
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ ml: 2, fontStyle: "italic" }}
                              >
                                Note: {result.notes}
                              </Typography>
                            )}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No abnormal results to display.
                    </Typography>
                  );
                })()}
              </Grid2>

              <Grid2 size={12}>
                <Divider />
              </Grid2>

              {/* Pathologist's Comments */}
              <Grid2 size={12}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Pathologist's Comments
                </Typography>
                {readOnly ? (
                  <Typography variant="body1">
                    {formData?.additionalNotes || "No comments provided."}
                  </Typography>
                ) : (
                  <TextField
                    multiline
                    rows={3}
                    value={formData?.additionalNotes || ""}
                    onChange={(e) => {
                      const updated = {
                        ...formData,
                        additionalNotes: e.target.value,
                      };
                      setFormData(updated);
                      handleFormDataChange(updated);
                    }}
                    placeholder="Enter your clinical interpretation and recommendations..."
                    fullWidth
                  />
                )}
              </Grid2>
            </Grid2>
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Reference Dialog */}
      <Dialog
        open={referenceDialog.open}
        onClose={handleReferenceClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Reference Text - {referenceDialog.title}</DialogTitle>
        <DialogContent>
          <div
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: theme.typography.body1.fontFamily,
              fontSize: theme.typography.body1.fontSize,
              lineHeight: theme.typography.body1.lineHeight,
            }}
            dangerouslySetInnerHTML={{
              __html:
                referenceDialog.text || "No reference text available for this test.",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReferenceClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid2>
  );
}

export default LaboratoryTestReportDisplay;